/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icon';
import { SegmentedButton, SegmentedButtonItem } from '@/components/ui/segmented-button';
import { cn } from '@/lib/utils';
import { isProductListingView, type ProductListingView } from '@/lib/product/view-preference';

export interface CategoryViewToggleProps {
    /** The active view. Controlled — the owner is `useProductListingView`. */
    value: ProductListingView;
    /** Called with the newly selected view. Never called with the current value. */
    onValueChange: (view: ProductListingView) => void;
    className?: string;
}

/**
 * Grid/list view toggle for the listing toolbar (CRCB2B-87).
 *
 * A consumer of the design-system segmented button (CRCB2B-642) at its icon-only content
 * variant — not a bespoke control. An earlier plan for this ticket would have hand-rolled one;
 * a view toggle *is* a segmented control, so that would have forked the primitive.
 *
 * Named `category-*` for consistency with its toolbar siblings (`category-sorting`,
 * `category-refinements`, `category-pagination`). Like `category-sorting`, nothing here is
 * category-specific — it is usable on any listing surface.
 *
 * ## Accessibility
 *
 * `SegmentedButton` fixes `type="single"`, which renders `role="radiogroup"`. A radiogroup with
 * no name announces its options without saying what they choose between, and icon-only segments
 * have no visible label to fall back on — so the group and both segments carry explicit names.
 * That is a requirement of the `segmented-buttons` spec, not a nicety.
 *
 * ## Deliberately no pending state
 *
 * Persistence happens in the background via a fetcher the owning hook fires. This control never
 * disables itself or shows a spinner: the AC asks the layout to update "immediately without
 * page reload", and gating the switch on a round trip is exactly what that rules out.
 */
export default function CategoryViewToggle({ value, onValueChange, className }: CategoryViewToggleProps): ReactElement {
    const { t } = useTranslation('common');

    return (
        <SegmentedButton
            value={value}
            // Radix ToggleGroup can emit an empty string when the active item is clicked again.
            // A listing always has a presentation, so an empty selection is discarded rather
            // than treated as "no view".
            onValueChange={(next) => {
                if (isProductListingView(next)) onValueChange(next);
            }}
            aria-label={t('productListing.view.label')}
            className={cn('flex-shrink-0', className)}
            // Deliberately no `data-testid`: the listing toolbar renders its controls twice —
            // once in the mobile block above the refinement panel, once inside the desktop
            // content column — and a duplicated test id makes `getByTestId` throw. Query by
            // role and accessible name instead, which is also how selection should be asserted
            // (`aria-checked`), since the sliding thumb never renders in jsdom.
        >
            <SegmentedButtonItem value="grid" content="icon" aria-label={t('productListing.view.grid')}>
                <Icon name="grid_view" size={24} />
            </SegmentedButtonItem>
            <SegmentedButtonItem value="list" content="icon" aria-label={t('productListing.view.list')}>
                <Icon name="view_list" size={24} />
            </SegmentedButtonItem>
        </SegmentedButton>
    );
}
